import type { ISchedule, ScheduleBuffer } from "@gettactic/api";
import { getScheduleBufferWindow } from "@gettactic/helpers/src/schedules/scheduleBuffer";
import { useMemo } from "react";

export interface RoomBufferConflict {
  scheduleId: string;
  title: string;
  start: Date;
  end: Date;
}

export interface RoomBufferConflictParams {
  /** The reservation the approval would book, as wall clock in the room's day. */
  start: Date;
  end: Date;
  buffer: ScheduleBuffer;
  /** Everything already booked in that room that day. */
  schedules: ISchedule[];
}

const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  aStart < bEnd && bStart < aEnd;

/**
 * The first reservation in the room whose own blocked window the requested one
 * would run into once its buffer is applied. Core answers an approval that hits
 * this with `buffer_overlaps_with_schedule` and books nothing, so it is worth
 * saying before the approver presses Approve — and core's own error names only
 * a schedule id, never a title.
 */
export const findRoomBufferConflict = ({
  start,
  end,
  buffer,
  schedules
}: RoomBufferConflictParams): RoomBufferConflict | null => {
  const before = Math.max(0, buffer.before_minutes);
  const after = Math.max(0, buffer.after_minutes);
  if (!before && !after) return null;

  const blockedStart = new Date(start.getTime() - before * 60_000);
  const blockedEnd = new Date(end.getTime() + after * 60_000);

  for (const schedule of schedules) {
    const other = getScheduleBufferWindow(schedule);
    if (
      !overlaps(blockedStart, blockedEnd, other.blockedStart, other.blockedEnd)
    ) {
      continue;
    }
    // The booked periods clashing outright is a plain double-booking, which
    // core reports on its own — only the time the buffer adds is news here.
    if (overlaps(start, end, other.start, other.end)) continue;

    return {
      scheduleId: schedule.id,
      title: schedule.title || "another reservation",
      start: other.start,
      end: other.end
    };
  }

  return null;
};

export const useRoomBufferConflict = (
  params: RoomBufferConflictParams
): RoomBufferConflict | null => {
  const { start, end, buffer, schedules } = params;
  return useMemo(
    () => findRoomBufferConflict({ start, end, buffer, schedules }),
    [start, end, buffer, schedules]
  );
};
